import { forwardRef, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { CodeCells, type CodeCellsHandle, CODE_LENGTH, formatCountdown } from '@foundation-bb/foundation-auth-ui';
import { Button, FieldError, LinkButton } from '@/ui';
import * as cellStyles from './CodeCells.css';
import * as s from '@/pages/LoginPage.css';

export const CodeStepForm = forwardRef<CodeCellsHandle, {
  email: string;
  code: string[];
  onCodeChange: (next: string[]) => void;
  onCodeComplete: (next: string[]) => void;
  onSubmit: (e: FormEvent) => void;
  submitting: boolean;
  error: string | null;
  countdown: number;
  resendAvailable: boolean;
  onResend: () => void;
  onUseDifferentEmail: () => void;
}>(function CodeStepForm({
  email, code, onCodeChange, onCodeComplete, onSubmit,
  submitting, error, countdown, resendAvailable, onResend, onUseDifferentEmail,
}, ref) {
  const { t } = useTranslation();
  return (
    <form id="login-code-form" onSubmit={onSubmit}>
      <p className={s.codeSentText}>
        {t('login.codeSent')} <strong id="login-code-email">{email}</strong>
      </p>

      <CodeCells
        ref={ref}
        code={code}
        onChange={onCodeChange}
        onComplete={onCodeComplete}
        disabled={submitting}
        classNames={{ container: cellStyles.cells }}
        cellAriaLabel={(i) => t('login.codeCellLabel', { n: i + 1 })}
        cellTestId={(i) => `code-input-${i}`}
      />

      {error && <FieldError>{error}</FieldError>}

      <Button id="login-code-submit" type="submit" variant="primary" block
              disabled={submitting || code.join('').length !== CODE_LENGTH}>
        {submitting ? t('login.verifying') : t('login.verify')}
      </Button>

      <div id="login-code-countdown" className={s.countdown}>
        {countdown > 0 ? formatCountdown(countdown) : t('login.codeExpired')}
      </div>

      <div className={s.codeActions}>
        {resendAvailable ? (
          <LinkButton id="login-code-resend" onClick={onResend} disabled={submitting}>
            {t('login.didntReceive')} {t('login.resend')}
          </LinkButton>
        ) : <span />}
        <LinkButton id="login-code-different-email" onClick={onUseDifferentEmail} disabled={submitting}>
          {t('login.useDifferentEmail')}
        </LinkButton>
      </div>
    </form>
  );
});
